import { Barangay, Precinct, Voter } from '../../types';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  DocumentData,
  DocumentSnapshot,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where
} from 'firebase/firestore';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';

const PrecinctList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [precincts, setPrecincts] = useState<Precinct[]>([]);
  const [barangays, setBarangays] = useState<Barangay[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadNext, setLoadNext] = useState(false);
  const [lastPrecinctRef, setLastPrecinctRef] = useState<DocumentSnapshot<DocumentData, DocumentData> | null>(null);
  const [selectedBarangay, setSelectedBarangay] = useState<string>('');

  const PAGE_SIZE = 50;

  // Fetch all barangays
  const fetchBarangays = async () => {
    try {
      const barangayRef = collection(db, 'barangays');
      const barangaySnapshot = await getDocs(query(barangayRef, orderBy('barangay')));
      
      const fetchedBarangays = barangaySnapshot.docs.map(doc => ({
        ...doc.data() as Barangay,
        id: doc.id
      }));

      setBarangays(fetchedBarangays);
    } catch (error) {
      console.error('Error fetching barangays:', error);
    }
  };

  const fetchPrecincts = async (
    lastDoc: DocumentSnapshot<DocumentData, DocumentData> | null,
    barangayFilter?: string
  ) => {
    try {
      const precinctRef = collection(db, 'precincts');
      let q = query(
        precinctRef,
        orderBy('barangay'),
        limit(PAGE_SIZE)
      );
      
      // Add barangay filter if provided
      if (barangayFilter) {
        q = query(q, where('barangay_id', '==', barangayFilter));
      }
      
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const precinctSnapshot = await getDocs(q);
      
      const fetchedPrecincts = precinctSnapshot.docs.map(doc => 
        doc.data() as Precinct
      );

      return {
        precincts: fetchedPrecincts,
        hasMore: fetchedPrecincts.length >= PAGE_SIZE,
        lastSnapshotDoc: precinctSnapshot.docs[precinctSnapshot.docs.length - 1]
      };
    } catch (error) {
      console.error('Error fetching precincts:', error);
      return {
        precincts: [],
        hasMore: false,
        lastSnapshotDoc: null
      };
    }
  };

  useEffect(() => {
    fetchBarangays();
  }, []);

  useEffect(() => {
    setLoading(true);
    // Reset precincts, hasMore, and lastPrecinctRef when barangay changes
    setPrecincts([]);
    setHasMore(true);
    setLastPrecinctRef(null);
    
    fetchPrecincts(null, selectedBarangay).then((result) => {
      setPrecincts(result.precincts); 
      setHasMore(result.hasMore);
      setLastPrecinctRef(result.lastSnapshotDoc);
      setLoading(false);
    });
  }, [selectedBarangay]);

  useEffect(() => {
    if (loadNext) {
      setLoading(true);
      fetchPrecincts(lastPrecinctRef, selectedBarangay).then((result) => {
        setPrecincts(prevPrecincts => [...prevPrecincts, ...result.precincts]); 
        setHasMore(result.hasMore);
        setLastPrecinctRef(result.lastSnapshotDoc);
        setLoading(false);
      });
      setLoadNext(false);
    }
  }, [loadNext, selectedBarangay]);
  
  const observer = useRef<IntersectionObserver | null>(null);
  
  const lastPrecinctElementRef = useCallback((node: HTMLTableRowElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setLoadNext(true);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const handlePrecinctClick = (precinct: Precinct) => {
    navigate(`/precincts/${precinct.barangay_id}/${precinct.precinct}`);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Precinct List</Typography>
        <FormControl variant="outlined" sx={{ minWidth: 250 }}>
          <InputLabel>Filter by Barangay</InputLabel>
          <Select
            value={selectedBarangay}
            onChange={(e) => setSelectedBarangay(e.target.value as string)}
            label="Filter by Barangay"
          >
            <MenuItem value="">
              <em>All Barangays</em>
            </MenuItem>
            {barangays.map((barangay) => (
              <MenuItem key={barangay.id} value={barangay.id}>
                {barangay.barangay} ({barangay.city_municipality})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Barangay</TableCell>
              <TableCell>Precinct</TableCell>
              <TableCell>City/Municipality</TableCell>
              <TableCell>Province</TableCell>
              <TableCell>District</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {precincts.map((precinct, index) => (
              <TableRow 
                key={`${precinct.precinct}_${precinct.barangay_id}`}
                hover
                onClick={() => handlePrecinctClick(precinct)}
                ref={index === precincts.length - 1 ? lastPrecinctElementRef : undefined}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{precinct.barangay}</TableCell>
                <TableCell>{precinct.precinct}</TableCell>
                <TableCell>{precinct.city_municipality}</TableCell>
                <TableCell>{precinct.province}</TableCell>
                <TableCell>{precinct.district}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && <Box display="flex" justifyContent="center" mt={2}><CircularProgress /></Box>}
      {!loading && precincts.length === 0 && <Typography align="center" mt={2}>No Precincts available</Typography>}
      {!loading && !hasMore && precincts.length > 0 && <Typography align="center" mt={2}>No more precincts to load. Total precincts: {precincts.length}</Typography>}
    </Box>
  );
};

export default PrecinctList;
